import contract from '../compile/c1-contract.json';
import { readItemField } from '../compile/item-field-reader.js';
import { readInventoryField, readInventoryRows, readItemUuidSlot, readLayerListEntryUuid } from '../compile/inventory-raw-readers.js';
import { createCatalogDefaultProviderReader } from '../compile/provider-catalog-order.js';
import { readNodeItemId } from '../compile/layer-cell-readers.js';
import { possessorEnvironmentDefinitionIds } from '../compile/possessor-compile.js';
import { encounterCreatureField } from '../../shared/array-types.js';
import { statusItemId } from '../statuses/expiry.js';
import { statusOriginDefinitionId } from '../statuses/status-origin.js';
import { statusInputSnapshot } from './input-snapshots.js';
import { ecId, ecLean, record, upper, type Row } from '../ability/context.js';

interface DefinitionReaders {
  definition: (member: Row, id: string) => Row | null;
  statuses: (member: Row) => readonly Row[];
  entries: (node: Row, key: string) => readonly unknown[];
}
interface DefinitionWatch { memberId: string; id: string; stamp: string }
export interface ImmunityDefinitionInputs {
  roots: string;
  definitions: readonly DefinitionWatch[];
  missing: readonly string[];
}
const uuid = /^[0-9A-F]{8}(?:-[0-9A-F]{4}){3}-[0-9A-F]{12}$/;
const definitionStamp = (node: Row | null): string => node
  ? JSON.stringify([readItemField(node, 'item_t_updated'), node.item_layer_types ?? null]) : '';
const grants = [...new Set([...contract.abilityTypes, ...contract.grants.inventoryAbilityTypes, 'feat', 'status_effect_permanent'])];
const environmentFields = ['encounter_weather', 'encounter_tempurature', 'encounter_wind', 'encounter_environment'];
const grantId = (entry: unknown): string => {
  const row = record(entry);
  return upper(row?.ability_id || readItemUuidSlot(row?.item_arr ?? row?.ability_arr) || readLayerListEntryUuid(entry));
};

/** Input discovery only. A removal can activate an inactive/default provider, so
 * follow its saved definition references without judging conditions or expiry.
 * The optional prior is caller-owned: compare relevant roots and watched catalog
 * definitions before traversing their closure again. AP/position never enter it.
 */
export function collectImmunityDefinitionInputs(roster: readonly Row[], read: DefinitionReaders,
  prior?: ImmunityDefinitionInputs): ImmunityDefinitionInputs {
  const roots: unknown[] = [], scopes: Array<{ member: Row; nodes: Row[]; ids: string[] }> = [];
  for (const member of roster) {
    const statuses = read.statuses(member);
    const inputs = statuses.map(statusInputSnapshot);
    // Watch every target definition, including a cold or formerly non-immunity
    // carrier: a catalog edit can make it an immunity provider at the same row id.
    roots.push([ecId(member), statuses.map((status, index) => [inputs[index], readItemField(status, 'status_t_updated'),
      definitionStamp(read.definition(member, statusItemId(status)))])]);
    if (!statuses.some(status => {
      const def = read.definition(member, statusItemId(status));
      return !!def && read.entries(def, 'immune_status_effect').length > 0;
    })) continue;
    const nodes = [member], ids = statuses.map(statusItemId), inventory = new Map<string, Row>();
    const visitInventory = (instances: readonly Row[]): void => {
      for (const instance of instances) {
        const id = upper(readInventoryField(instance, 'inventory_item_id'));
        inventory.set(upper(readInventoryField(instance, 'inventory_id')), instance);
        ids.push(id); nodes.push(instance); visitInventory(readInventoryRows(instance, 'inventory_in_container'));
      }
    };
    visitInventory(readInventoryRows(member, 'inventory'));
    for (const status of statuses) {
      const origin = statusOriginDefinitionId(status, inventory.get(upper(readItemField(status, 'status_origin_id'))));
      if (origin) ids.push(origin);
    }
    if (ecLean(member, 'item_type') === 'creature') ids.push(upper(ecLean(member, 'item_id')));
    ids.push(...possessorEnvironmentDefinitionIds(member));
    // The retained native-weapon default is read by the status compiler even on
    // an unarmed holder. It is a definition input, never a speculative compile.
    ids.push(contract.equippedCounts.engineFistMarkerId);
    const provider = createCatalogDefaultProviderReader({ catalogRows: contract.catalogRows,
      nodeItemId: readNodeItemId, resolveCarrierRef: id => { ids.push(upper(id)); return read.definition(member, upper(id)); } });
    for (const node of provider.providers(member)) if (record(node)) nodes.push(node as Row);
    const conditions: unknown[] = [];
    for (const key of grants) for (const entry of read.entries(member, key)) {
      const row = record(entry), nested = record(row?.item_arr ?? row?.ability_arr);
      ids.push(grantId(entry));
      conditions.push([grantId(entry), row ? readItemField(row, 'source') : null,
        row ? readItemField(row, 'passive_conditions_active') : null]);
      if (nested) nodes.push(nested);
    }
    // Only definition-bearing layer slots are fingerprinted. No held member,
    // inventory tree, encounter or computed cluster is copied or serialized.
    roots.push([ecId(member), ids, environmentFields.map(key => encounterCreatureField(member.encounter_creatures, key) ?? null),
      conditions, nodes.map(node => definitionStamp(node))]);
    scopes.push({ member, nodes, ids });
  }
  const rootKey = JSON.stringify(roots);
  const members = new Map(roster.map(member => [ecId(member), member]));
  if (prior?.roots === rootKey && prior.definitions.every(watch =>
    members.has(watch.memberId) && definitionStamp(read.definition(members.get(watch.memberId)!, watch.id)) === watch.stamp)) return prior;

  const missing = new Set<string>(), definitions: DefinitionWatch[] = [];
  for (const { member, nodes, ids } of scopes) {
    const seenIds = new Set<string>(), seenNodes = new Set<Row>(), pending = [...nodes];
    const need = (raw: string): void => {
      const id = upper(raw); if (!uuid.test(id) || seenIds.has(id)) return;
      seenIds.add(id);
      const def = read.definition(member, id);
      definitions.push({ memberId: ecId(member), id, stamp: definitionStamp(def) });
      if (def) pending.push(def); else missing.add(id);
    };
    ids.forEach(need);
    const references = (value: unknown): void => {
      if (typeof value === 'string') {
        // Layer cells also carry UUIDs in scoring tokens and authored JSON.
        // Reading these references does not interpret or execute their rules.
        for (const match of value.matchAll(/[0-9A-F]{8}(?:-[0-9A-F]{4}){3}-[0-9A-F]{12}/gi)) need(match[0]);
      } else if (value && typeof value === 'object') {
        const node = record(value);
        if (node && (record(node.items) || record(node.item_layer_types))) {
          const id = readNodeItemId(node); if (id) need(id);
          pending.push(node); return;
        }
        for (const [key, child] of Object.entries(value)) {
          if (uuid.test(upper(key))) need(key);
          if (key !== 'source' && key !== 'href_id') references(child);
        }
      }
    };
    for (let at = 0; at < pending.length; at++) {
      const node = pending[at]!; if (seenNodes.has(node)) continue;
      seenNodes.add(node);
      for (const slot of Object.values(record(node.item_layer_types) ?? {})) references(slot);
      // Flattened native/gear grant slots can outlive their authored bucket.
      for (const key of grants) for (const entry of read.entries(node, key)) {
        const grant = record(entry), nested = record(grant?.item_arr ?? grant?.ability_arr);
        need(grantId(entry));
        if (nested) pending.push(nested);
      }
    }
  }
  return { roots: rootKey, definitions, missing: [...missing] };
}
